import { useEffect, useRef, useState } from 'react';

import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { LibraryBookObjectProps } from './LibraryBookObject.types';
import { LibraryBookObject3D, preloadBookPages } from './LibraryBookObject3D';

/** How long the pointer rests on a book before it opens and turns its pages. */
const OPEN_AFTER_MS = 1200;

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * A Library book drawn as a book, in a 3:4 frame: the cover on a block of pages
 * whose edges show, as thick as the real book. Used wherever a Library book is
 * shown — a shelf card, the book's own page — so it looks and behaves the same.
 *
 * Web adds the hand: the book tilts toward the pointer with a glare across the
 * cover, and after a moment of hovering it opens and turns through its real
 * pages to its figures (`preview`). Native draws it still.
 */
export const LibraryBookObject = ({
  cover,
  title,
  pages,
  preview,
  className,
  testID,
}: LibraryBookObjectProps) => {
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hoverId = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);

  // As thick as the real book against its width: ~1px per 20 pages (a 500-page
  // volume ≈ 25px on a ~190px cover), between 8 and 30px.
  const depth = Math.round(Math.min(30, Math.max(8, (pages ?? 300) / 20)));

  // The cover as seen from the front: the art, the joint groove and the glare.
  const face = (
    <View className="absolute inset-0 overflow-hidden rounded-l-[2px] rounded-r-[4px] bg-secondary-100 shadow-lg web:shadow-none">
      {cover}
      {/* The joint: the groove pressed into a hardback's board beside the spine —
          a soft shadow into it, a crease, and light catching its outer lip. */}
      <View className="absolute inset-0 hidden web:flex web:bg-[linear-gradient(to_right,rgba(0,0,0,0.28),rgba(0,0,0,0.08)_3%,rgba(255,255,255,0.05)_5.2%,rgba(0,0,0,0.22)_6.4%,rgba(0,0,0,0.38)_7%,rgba(0,0,0,0.12)_7.6%,rgba(255,255,255,0.12)_8.3%,rgba(255,255,255,0.03)_9.5%,transparent_12%)]" />
      <View className="absolute bottom-0 left-0 top-0 w-[7%] bg-black/15 web:hidden" />
      <View
        className={cn(
          'absolute inset-0 hidden opacity-0 transition-opacity duration-300 web:flex web:group-hover/book:opacity-100',
          'web:bg-[radial-gradient(circle_at_var(--glare-x,50%)_var(--glare-y,0%),rgba(255,255,255,0.1),transparent_65%)]',
        )}
      />
    </View>
  );

  return (
    <View
      testID={testID}
      className={cn(
        'group/book relative aspect-[3/4] w-full web:[perspective:1100px]',
        // Above its neighbours while it is lifted or open.
        (hovered || open) && 'web:z-10',
        className,
      )}
      webProps={{
        onMouseEnter: () => {
          if (reducedMotion()) return;
          setHovered(true);
          if (!preview) return;
          // Opens after a moment of hovering, and only onto pages already loaded.
          const ready = Promise.all([
            preloadBookPages(preview.src),
            new Promise((r) => (timer.current = setTimeout(r, OPEN_AFTER_MS))),
          ]);
          const hover = (hoverId.current += 1);
          ready.then(() => hover === hoverId.current && setOpen(true)).catch(() => {});
        },
        onMouseLeave: (e) => {
          clearTimeout(timer.current);
          hoverId.current += 1;
          setHovered(false);
          setOpen(false);
          e.currentTarget.style.setProperty('--book-rx', '0deg');
          e.currentTarget.style.setProperty('--book-ry', '0deg');
        },
        // Written straight to CSS variables: the tilt follows the pointer
        // without re-rendering the book.
        onPointerMove: (e) => {
          if (e.pointerType !== 'mouse' || reducedMotion()) return;
          const el = e.currentTarget;
          const r = el.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          el.style.setProperty('--book-ry', `${((x - 0.5) * 36).toFixed(2)}deg`);
          el.style.setProperty('--book-rx', `${((0.5 - y) * 24).toFixed(2)}deg`);
          el.style.setProperty('--glare-x', `${(x * 100).toFixed(1)}%`);
          el.style.setProperty('--glare-y', `${(y * 100).toFixed(1)}%`);
        },
      }}>
      <View
        className={cn(
          'absolute inset-0 web:[transform-style:preserve-3d]',
          'web:transition-transform web:duration-500 web:ease-out',
          'web:[transform:translateX(var(--book-shift))_translateZ(var(--book-lift))_rotateX(calc(var(--book-rx,0deg)+var(--book-lay)))_rotateY(var(--book-ry,0deg))_scale(var(--book-scale))]',
          'web:[--book-shift:0%] web:[--book-lift:0px] web:[--book-lay:0deg] web:[--book-scale:1]',
          hovered && 'web:[--book-lift:24px]',
          open && 'web:[--book-shift:55%] web:[--book-lay:22deg] web:[--book-scale:1.1]',
        )}>
        <LibraryBookObject3D
          face={face}
          art={cover}
          title={title}
          depth={depth}
          open={open}
          preview={preview}
        />
      </View>
    </View>
  );
};
(LibraryBookObject as { displayName?: string }).displayName = 'LibraryBookObject';
